import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import classNames from 'classnames'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { CompanyNameValues } from 'core/application/adminSignUp/common/models'

import InputMask from 'common/components/dataEntry/formik/FormikInputMaskField'
import Input from 'common/components/dataEntry/formik/FormikStyledInputField'
import Alert from 'common/components/feedback/Alert'
import Icon from 'common/components/general/Icon'
import CategoryItemPageHeader from 'common/components/onBoarding/Common'
import { identificationNumberRegex } from 'common/components/utils/InputUtils'

import { useBreakPoints } from 'common/hooks'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import { Radio } from '../../common/components/dataEntry/formik/FormikRadioField'
import useCommandRequest from '../../common/hooks/useCommandRequest'
import WizardNextButton from './WizardNextButton'

import './CompanyDetailsStyles.less'

interface CompanyNamePros extends BaseNavigationProps<CompanyNameValues> {
  onSubmitFormHandler: (values: CompanyNameValues) => Promise<any>
  message: string
  description: string
  numberOfQuestions: number
  currentQuestion: number
  showIsAleQuestion?: boolean
}

const CompanyName: React.FC<CompanyNamePros> = (props) => {
  const { t: translate } = useTranslation()

  const [sendCommand, isLoading] = useCommandRequest()
  const { isLg } = useBreakPoints()

  const onSubmit = async (values: CompanyNameValues) => {
    const result = await sendCommand(() => props.onSubmitFormHandler(values))

    if (result.completed) {
      props.goNext(props.name)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate, props.showIsAleQuestion)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form
          onSubmit={formik.handleSubmit}
          autoComplete="off"
          className="full-height relative t-admin-signup-company-name"
        >
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 8 }}>
              <CategoryItemPageHeader
                title={translate('admin-signup.company-details.title')}
                numberOfQuestions={props.numberOfQuestions}
                currentQuestion={props.currentQuestion}
                question={translate('admin-signup.company-details.company-name.question')}
              />
            </Col>
          </Row>
          <Row className="form__row mt-16">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <div className={classNames({ 'pl-12': isLg })}>
                <Alert
                  type="info"
                  className={'t-admin-signup-company-name-info'}
                  showIcon
                  message={props.message}
                  description={props.description}
                />
              </div>
            </Col>
          </Row>
          <Row className="form__row mt-32">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <Input
                label={translate('admin-signup.company-details.company-name.company-name-label')}
                name="companyName"
                placeholder={translate('admin-signup.company-details.company-name.company-name-placeholder')}
              />
            </Col>
          </Row>
          <Row className="form__row mt-24">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
              <InputMask
                label={translate('admin-signup.company-details.company-name.identification-number-label')}
                name="ein"
                mask="00-0000000"
                placeholder="12-3456789"
                className="formik_input identification_number"
                suffix={<Icon type="fa fa-hashtag" className="gray-6" />}
              />
            </Col>
          </Row>
          {props.showIsAleQuestion && (
            <Row className="form__row mt-32">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
                <div className={'mb-16'}>
                  <label className={'text-bold'}>
                    On average, did you have 50 or more full-time or full-time equivalent W-2 employees over the
                    previous twelve months?
                  </label>
                </div>
                <Radio.Group name="isAle" defaultValue={props.state.isAle}>
                  <Row gutter={10}>
                    <Col xs={12} sm={8}>
                      <Radio.Button value={true}>Yes</Radio.Button>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Radio.Button value={false}>No</Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>
          )}
          {formik.values.isAle && (
            <Row className="form__row mt-16">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 16, offset: 3 }} xl={{ span: 12 }}>
                <Alert
                  type="info"
                  className={'t-admin-signup-company-name-ale-info'}
                  showIcon
                  message={'Information about ALEs'}
                  description={
                    'As an ALE, you are responsible for compliance with additional regulations, such as the Employer Information Reporting Provision and adherence to the Family Medical Leave Act (FMLA), which are not supported by the PeopleKeep software.'
                  }
                />
              </Col>
            </Row>
          )}
          <Row className="bottom__actions">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 3 }}>
              <WizardNextButton
                disabled={!formValidator(translate, props.showIsAleQuestion).isValidSync(formik.values)}
                loading={isLoading}
              />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

const formValidator = (translate: any, showIsAleQuestion: boolean) =>
  Yup.object({
    companyName: Yup.string().trim().nullable().required(translate('general.required')),
    ein: Yup.string()
      .trim()
      .nullable()
      .required(translate('general.required'))
      .matches(identificationNumberRegex, translate('admin-signup.company-details.errors.identification-number-regex')),
    isAle: showIsAleQuestion ? Yup.boolean().required(translate('general.required')) : Yup.boolean(),
  })

export default CompanyName
